import { useMyProfile, useUpdateProfile } from '@hobbytools/data';
import { AppShell, friendlyError, useAuth } from '@hobbytools/platform';
import { Banner, Button, Card, TextField } from '@hobbytools/ui';
import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { linkClass } from './AuthLayout';

/** /profile — what other players see, plus account basics. */
export function Profile() {
  const { user } = useAuth();
  const profile = useMyProfile(user?.id);
  const update = useUpdateProfile(user?.id);
  const [displayName, setDisplayName] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (profile.data) setDisplayName(profile.data.display_name);
  }, [profile.data]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await update.mutateAsync({ displayName });
      setSaved(true);
    } catch (err) {
      setError(friendlyError(err));
    }
  };

  return (
    <AppShell current="hub">
      <div className="flex max-w-2xl flex-col gap-6">
        <header>
          <p className="eyebrow">Your account</p>
          <h1 className="heading-display text-3xl">Profile</h1>
        </header>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {saved ? <Banner tone="success">Saved. Other players see your new name right away.</Banner> : null}
        <Card>
          <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
            <TextField
              label="Display name"
              description="Shown to players in your groups and on battle records."
              isRequired
              maxLength={60}
              value={displayName}
              onChange={setDisplayName}
            />
            <Button
              type="submit"
              className="self-start"
              isDisabled={
                update.isPending || !displayName.trim() || displayName.trim() === profile.data?.display_name
              }
            >
              Save profile
            </Button>
          </form>
        </Card>
        <Card className="flex flex-col gap-2">
          <h2 className="heading-display text-xl">Sign-in</h2>
          <p className="text-sm text-fg-muted">Signed in as {user?.email}.</p>
          <Link className={`${linkClass} text-sm`} to="/reset-password">
            Change your password
          </Link>
        </Card>
      </div>
    </AppShell>
  );
}
